import BotonManualHeader from "../../components/BotonManualHeader";
import React, { useState, useEffect } from "react";
import firebaseApp from "../../firebase/credenciales";
import {
  getFirestore,
  collection,
  getDocs,
  addDoc,
  setDoc,
  doc,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import {
  Container,
  Card,
  Form,
  Button,
  Table,
  Spinner,
  Badge,
  Alert,
  Row,
  Col,
  ProgressBar,
} from "react-bootstrap";
import {
  FaUpload,
  FaFileExcel,
  FaDownload,
  FaCloudUploadAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaEye,
  FaExclamationTriangle,
} from "react-icons/fa";
import Swal from "sweetalert2";
import { logBusinessAction } from "../../services/AuditService";

const db = getFirestore(firebaseApp);

function VacacionesMasivas({ usuario }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tipoVacacion, setTipoVacacion] = useState("gozadas");

  // Estados para vista previa y validación
  const [previewData, setPreviewData] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  // Estados para mejoras
  const [employeeDNIs, setEmployeeDNIs] = useState(new Set());
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStats, setUploadStats] = useState({
    total: 0,
    processed: 0,
    successful: 0,
    failed: 0
  });

  // Helper para convertir cualquier valor a string de forma segura para React
  const formatCellValue = (value) => {
    if (value === null || value === undefined) return '';
    if (value instanceof Date) {
      // Asegurarse de que es una fecha válida
      if (isNaN(value.getTime())) return '';

      // Formatear como dd/mm/yyyy con ceros a la izquierda
      const day = String(value.getDate()).padStart(2, '0');
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const year = value.getFullYear();
      return `${day}/${month}/${year}`;
    }
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    setResults([]);
    setPreviewData([]);
    setShowPreview(false);
    setValidationErrors([]);

    // Auto-cargar preview si hay archivo
    if (file) {
      handlePreview(file);
    }
  };

  // Cargar DNIs de empleados activos al montar el componente
  useEffect(() => {
    loadEmployeeDNIs();
  }, [usuario]);

  const loadEmployeeDNIs = async () => {
    setLoadingEmployees(true);
    try {
      const ruc = usuario.ruc || usuario.rucEmpresa;
      const usersRef = collection(db, "users");
      const q = query(
        usersRef,
        where("rucEmpresa", "==", ruc),
        where("activo", "==", true)
      );

      const snapshot = await getDocs(q);
      const dnis = new Set();
      snapshot.forEach(doc => {
        const dni = doc.data().dni;
        if (dni) dnis.add(String(dni));
      });

      setEmployeeDNIs(dnis);
      console.log(`✓ Cargados ${dnis.size} empleados activos para validación`);
    } catch (error) {
      console.error("Error cargando empleados:", error);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const clean = (v) => {
    if (typeof v === "string") return v.trim();
    if (typeof v === "number") return String(v).trim();
    return v ?? "";
  };

  // Parsear fecha en formato dd/mm/yyyy o objeto Date de Excel
  const parseExcelDate = (value) => {
    if (!value) return null;

    // Si ya es un objeto Date (cellDates: true)
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    // Si es string en formato dd/mm/yyyy
    if (typeof value === "string") {
      const parts = value.split("/");
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Mes base 0
        const year = parseInt(parts[2], 10);
        const date = new Date(year, month, day);
        return isNaN(date.getTime()) ? null : date;
      }
    }

    // Si es número serial de Excel
    if (typeof value === "number") {
      const date = new Date((value - 25569) * 86400 * 1000);
      return isNaN(date.getTime()) ? null : date;
    }

    return null;
  };

  // Validar DNI
  const isValidDNI = (dni) => {
    const dniStr = String(dni).trim();
    return /^\d{8}$/.test(dniStr);
  };

  // Validar fila de datos
  const validateRow = (row, excelRow, tipo, columnMap = null, employeeDNIs = null) => {
    const errors = [];
    const rowNumber = excelRow; // Número de fila real en Excel

    // Helper para obtener valor de columna
    const getCol = (fieldName, defaultIndex) => {
      const index = columnMap?.[fieldName] !== undefined ? columnMap[fieldName] : defaultIndex;
      return row[index];
    };

    if (tipo === "gozadas") {
      // Usar columnMap si está disponible, sino usar posiciones por defecto
      const dni = getCol('dni', 0);
      const nombre = getCol('nombre', 1);
      const estado = getCol('estado', 2);
      const fechaInicio = getCol('fechaInicio', 3);
      const fechaFin = getCol('fechaFin', 4);
      const motivo = getCol('motivo', 5);
      const periodo = getCol('periodo', 6);
      const totalDias = getCol('totalDias', 7);
      const pdfUrl = getCol('pdfUrl', 8);

      // Validar DNI formato
      if (!dni || !isValidDNI(dni)) {
        errors.push(`Fila ${rowNumber}: DNI inválido (debe ser 8 dígitos)`);
      }

      // Validar que el empleado exista en la base de datos
      if (employeeDNIs && employeeDNIs.size > 0 && dni) {
        if (!employeeDNIs.has(String(dni))) {
          errors.push(`Fila ${rowNumber}: Empleado con DNI ${dni} no existe en la base de datos`);
        }
      }

      // Validar nombre (obligatorio)
      if (!nombre || String(nombre).trim() === "") {
        errors.push(`Fila ${rowNumber}: Nombre completo es requerido`);
      }

      // Estado es opcional
      // if (!estado || String(estado).trim() === "") {
      //   errors.push(`Fila ${rowNumber}: Estado es requerido`);
      // }

      // Validar fechas
      const fechaInicioDate = parseExcelDate(fechaInicio);
      const fechaFinDate = parseExcelDate(fechaFin);

      if (!fechaInicioDate) {
        errors.push(`Fila ${rowNumber}: Fecha inicio inválida o vacía`);
      }
      if (!fechaFinDate) {
        errors.push(`Fila ${rowNumber}: Fecha fin inválida o vacía`);
      }

      if (fechaInicioDate && fechaFinDate) {
        if (fechaInicioDate > fechaFinDate) {
          errors.push(`Fila ${rowNumber}: Fecha inicio no puede ser mayor que fecha fin`);
        } else {
          // Validar coherencia de días calculados
          const diffTime = Math.abs(fechaFinDate - fechaInicioDate);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 porque es inclusivo
          const diasReportados = Number(totalDias);

          if (!isNaN(diasReportados) && diasReportados > 0 && diffDays !== diasReportados) {
            // Esto podría ser un warning en lugar de error si consideran días hábiles vs calendario.
            // Para simplificar y robustez, asumiremos días calendario por defecto, o solo advertimos.
            // El usuario pidió "Validar que días coincidan con diferencia de fechas". Lo haré error para asegurar consistencia.
            errors.push(`Fila ${rowNumber}: Inconsistencia en días. Fechas indican ${diffDays} días, pero 'Total Días' dice ${diasReportados}`);
          }
        }

        // Validar coherencia con periodo (opcional, flexible)
        if (periodo) {
          const yearInicio = fechaInicioDate.getFullYear();
          const yearPeriodo = Number(periodo);
          // Permitimos que se tomen vacaciones del 2023 en 2024 o 2025, pero no al revés (tomar del 2025 en 2023)
          // Aunque "Periodo" suele referirse al periodo ganado.
          // Simplemente validaremos que el periodo sea un año razonable (ej. > 2000)
          if (!isNaN(yearPeriodo) && (yearPeriodo < 2000 || yearPeriodo > 2100)) {
            errors.push(`Fila ${rowNumber}: Periodo ${periodo} parece inválido`);
          }
        }
      }

    } else {
      // Validar pendientes
      const dni = getCol('dni', 0);
      const nombre = getCol('nombre', 1);
      const estado = getCol('estado', 2);
      const fechaInicioPeriodo = getCol('fechaInicioPeriodo', 3);
      const fechaFinPeriodo = getCol('fechaFinPeriodo', 4);
      const fechaVencimiento = getCol('fechaVencimiento', 5);
      const periodo = getCol('periodo', 6);
      const diasGanados = getCol('diasGanados', 7);
      const observaciones = getCol('observaciones', 8);
      const regimen = getCol('regimen', 9);

      // Validar DNI formato
      if (!dni || !isValidDNI(dni)) {
        errors.push(`Fila ${rowNumber}: DNI inválido (debe ser 8 dígitos)`);
      }

      // Validar que el empleado exista en la base de datos
      if (employeeDNIs && employeeDNIs.size > 0 && dni) {
        if (!employeeDNIs.has(String(dni))) {
          errors.push(`Fila ${rowNumber}: Empleado con DNI ${dni} no existe en la base de datos`);
        }
      }

      // Nombre y estado son opcionales

      // Validar fechas
      const fechaInicioPeriodoDate = parseExcelDate(fechaInicioPeriodo);
      const fechaFinPeriodoDate = parseExcelDate(fechaFinPeriodo);
      const fechaVencimientoDate = parseExcelDate(fechaVencimiento);

      if (!fechaInicioPeriodoDate) {
        errors.push(`Fila ${rowNumber}: Fecha inicio periodo inválida o vacía`);
      }
      if (!fechaFinPeriodoDate) {
        errors.push(`Fila ${rowNumber}: Fecha fin periodo inválida o vacía`);
      }
      if (!fechaVencimientoDate) {
        errors.push(`Fila ${rowNumber}: Fecha vencimiento inválida o vacía`);
      }

      if (fechaInicioPeriodoDate && fechaFinPeriodoDate && fechaInicioPeriodoDate > fechaFinPeriodoDate) {
        errors.push(`Fila ${rowNumber}: Fecha inicio periodo no puede ser mayor que fecha fin periodo`);
      }

      if (fechaFinPeriodoDate && fechaVencimientoDate && fechaVencimientoDate < fechaFinPeriodoDate) {
        errors.push(`Fila ${rowNumber}: Fecha vencimiento no puede ser anterior al fin del periodo`);
      }

      if (periodo && fechaVencimientoDate) {
        const yearPeriodo = Number(periodo);
        const yearVencimiento = fechaVencimientoDate.getFullYear();

        // Validar que vencimiento no sea anterior al periodo (margen de error basico)
        if (!isNaN(yearPeriodo) && yearVencimiento < yearPeriodo) {
          errors.push(`Fila ${rowNumber}: Fecha vencimiento (${yearVencimiento}) es incoherente con el periodo (${yearPeriodo})`);
        }
      }

      // Validar periodo
      if (!periodo || String(periodo).trim() === "") {
        errors.push(`Fila ${rowNumber}: Periodo es requerido`);
      }

      // Validar días ganados
      const dias = Number(diasGanados);
      if (isNaN(dias) || dias <= 0) {
        errors.push(`Fila ${rowNumber}: Días ganados debe ser un número positivo`);
      }

      // Validar régimen
      if (!regimen || String(regimen).trim() === "") {
        errors.push(`Fila ${rowNumber}: Régimen es requerido`);
      }
    }


    return errors;
  };

  // Detectar duplicados en el archivo
  const checkDuplicates = (data, columnMap, startRow, tipo) => {
    const seen = new Map(); // key -> primera fila donde aparece
    const duplicates = [];

    data.forEach((row, idx) => {
      const dniIdx = columnMap?.dni !== undefined ? columnMap.dni : 0;
      const periodoIdx = columnMap?.periodo !== undefined ? columnMap.periodo : 6;

      const dni = String(row[dniIdx] || "").trim();
      const periodo = String(row[periodoIdx] || "").trim();

      if (dni && periodo) {
        const key = `${dni}_${periodo}`;

        if (seen.has(key)) {
          const firstRow = seen.get(key);
          duplicates.push({
            row: startRow + idx + 1,
            firstOccurrence: firstRow,
            dni,
            periodo,
            message: `Fila ${startRow + idx + 1}: Registro duplicado (DNI ${dni}, Periodo ${periodo}). Primera aparición en fila ${firstRow}`
          });
        } else {
          seen.set(key, startRow + idx + 1);
        }
      }
    });

    return duplicates;
  };

  // Función para cargar y mostrar vista previa
  const handlePreview = async (file) => {
    if (!file) return;
    setLoading(true);

    const fileReader = new FileReader();
    fileReader.onload = async (e) => {
      try {
        const workbook = XLSX.read(e.target.result, {
          type: "binary",
          cellDates: true,
        });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];

        // Leer todas las filas sin saltar ninguna para detectar dónde empiezan los datos
        const allData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          range: 0, // Leer desde la primera fila
          defval: "",
        });

        // Detectar automáticamente dónde empiezan los datos reales
        let startRow = 0;

        // Buscar la primera fila que parece tener datos (DNI numérico de 8 dígitos)
        for (let i = 0; i < Math.min(5, allData.length); i++) {
          const row = allData[i];
          if (row && row.length > 0) {
            const firstCell = String(row[0]).trim();

            // Si la primera celda es un DNI válido (8 dígitos), esta es la fila de datos
            if (/^\d{8}$/.test(firstCell)) {
              startRow = i;
              console.log(`✓ Datos detectados en fila ${i + 1} (primera celda: ${firstCell})`);
              break;
            }

            // Si contiene texto como "DNI", "Nombre", etc., es encabezado, seguir buscando
            if (/dni|nombre|estado|fecha|periodo/i.test(firstCell)) {
              console.log(`⊗ Fila ${i + 1} parece ser encabezado (contiene: ${firstCell})`);
              continue;
            }
          }
        }

        // Si no encontramos datos en las primeras 5 filas, asumir que empiezan en fila 3 (índice 2)
        if (startRow === 0 && allData.length > 2) {
          startRow = 2;
          console.log(`⚠ No se detectaron datos automáticamente, usando fila 3 por defecto`);
        }

        // Mapear columnas basándose en los encabezados
        let columnMap = null;
        const headerRow = startRow > 0 ? allData[startRow - 1] : null;

        if (headerRow) {
          console.log(`📋 Encabezados detectados:`, headerRow);

          // Crear mapa de columnas basado en nombres de encabezados
          columnMap = {};
          headerRow.forEach((header, index) => {
            const headerStr = String(header || "").toLowerCase().trim();

            if (/^dni$/i.test(headerStr)) columnMap.dni = index;
            else if (/nombre|name/i.test(headerStr)) columnMap.nombre = index;
            else if (/estado|status/i.test(headerStr)) columnMap.estado = index;
            else if (/fecha.*inicio|inicio|start/i.test(headerStr)) columnMap.fechaInicio = index;
            else if (/fecha.*fin|fin|end/i.test(headerStr)) columnMap.fechaFin = index;
            else if (/motivo|reason/i.test(headerStr)) columnMap.motivo = index;
            else if (/periodo|period/i.test(headerStr)) columnMap.periodo = index;
            else if (/d[ií]as|total.*d[ií]as|days/i.test(headerStr)) columnMap.totalDias = index;
            else if (/pdf|url|link/i.test(headerStr)) columnMap.pdfUrl = index;
            else if (/observ/i.test(headerStr)) columnMap.observaciones = index;
            else if (/r[ée]gimen|regimen/i.test(headerStr)) columnMap.regimen = index;
          });

          console.log(`✓ Mapa de columnas:`, columnMap);
        }

        // Extraer solo las filas de datos
        const data = allData.slice(startRow);

        console.log(`📊 Procesando ${data.length} filas de datos desde fila ${startRow + 1}`);

        const preview = [];
        const allErrors = [];

        for (let i = 0; i < data.length; i++) {
          const row = data[i];

          // Verificar si la fila tiene datos
          if (row && row.length > 0) {
            // Usar columnMap si está disponible, sino usar posición 0 por defecto
            const dniIndex = columnMap?.dni !== undefined ? columnMap.dni : 0;
            const dni = String(row[dniIndex] || "").trim();

            // Si no hay DNI, asumimos que llegamos al final de los datos reales
            // Esto evita procesar miles de filas vacías en Excel
            if (!dni) {
              console.log(`⊗ Fila ${startRow + i + 1} sin DNI - deteniendo lectura`);
              break; // Detener el procesamiento
            }

            const excelRow = startRow + i + 1; // Número de fila real en Excel

            // Validar la fila (ahora pasamos columnMap y employeeDNIs)
            const rowErrors = validateRow(row, excelRow, tipoVacacion, columnMap, employeeDNIs);
            allErrors.push(...rowErrors);

            // Agregar a preview con información del estado de validación
            preview.push({
              index: i,
              row: row,
              isValid: rowErrors.length === 0,
              errors: rowErrors,
              excelRow: excelRow,
              columnMap: columnMap, // Guardar el mapa para usar en la vista previa
            });
          }
        }

        // Detectar duplicados en el archivo
        const foundDuplicates = checkDuplicates(data, columnMap, startRow, tipoVacacion);
        setDuplicates(foundDuplicates);

        // Agregar advertencias de duplicados a los errores
        if (foundDuplicates.length > 0) {
          foundDuplicates.forEach(dup => {
            allErrors.push(dup.message);
          });
          console.log(`⚠ Detectados ${foundDuplicates.length} registros duplicados`);
        }

        setPreviewData(preview);
        setValidationErrors(allErrors);
        setShowPreview(true);
      } catch (error) {
        console.error("Error al leer archivo:", error);
        alert("Error al leer el archivo. Verifica que sea un Excel válido.");
      } finally {
        setLoading(false);
      }
    };

    fileReader.readAsBinaryString(file);
  };


  // Modal de confirmación antes de procesar
  const handleConfirmUpload = async () => {
    const validCount = previewData.filter(item => item.isValid).length;
    const invalidCount = previewData.filter(item => !item.isValid).length;
    const duplicateCount = duplicates.length;

    const result = await Swal.fire({
      title: '¿Confirmar Carga de Vacaciones?',
      html: `
        <div class="text-start">
          <p><strong>Resumen de la carga:</strong></p>
          <ul style="list-style: none; padding-left: 0;">
            <li style="margin: 8px 0;">✓ Registros válidos: <strong style="color: #28a745;">${validCount}</strong></li>
            ${invalidCount > 0 ? `<li style="margin: 8px 0;">✗ Registros con errores: <strong style="color: #dc3545;">${invalidCount}</strong> (se omitirán)</li>` : ''}
            ${duplicateCount > 0 ? `<li style="margin: 8px 0;">⚠ Duplicados detectados: <strong style="color: #ffc107;">${duplicateCount}</strong></li>` : ''}
          </ul>
          <p class="text-muted small mt-3" style="font-size: 0.9em;">
            Solo se procesarán los ${validCount} registros válidos.
            ${duplicateCount > 0 ? '<br>Los duplicados se procesarán solo una vez.' : ''}
          </p>
        </div>
      `,
      icon: duplicateCount > 0 ? 'warning' : 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, procesar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
      width: '500px'
    });

    if (result.isConfirmed) {
      await handleUpload();
    }
  };

  const handleUpload = async () => {
    if (previewData.length === 0) {
      alert("No hay datos para procesar. Por favor carga un archivo primero.");
      return;
    }

    // Filtrar solo las filas válidas
    const validRows = previewData.filter(item => item.isValid);

    if (validRows.length === 0) {
      alert("No hay filas válidas para procesar. Por favor corrige los errores.");
      return;
    }

    setLoading(true);
    setShowPreview(false);

    const totalRows = validRows.length;
    setUploadStats({ total: totalRows, processed: 0, successful: 0, failed: 0 });
    setUploadProgress(0);

    const newResults = [];
    let successCount = 0;
    let failedCount = 0;

    const BATCH_SIZE = 50;
    const startTime = performance.now();

    for (let i = 0; i < totalRows; i += BATCH_SIZE) {
      const batch = validRows.slice(i, i + BATCH_SIZE);

      // Procesar lote en paralelo
      await Promise.all(batch.map(async (item) => {
        const row = item.row;
        let registro = {};

        try {
          if (tipoVacacion === "gozadas") {
            const colMap = item.columnMap;
            const getVal = (field, defaultIdx) => {
              const idx = colMap?.[field] !== undefined ? colMap[field] : defaultIdx;
              return row[idx];
            };

            const dniRaw = getVal('dni', 0);
            const nombreRaw = getVal('nombre', 1);
            const estadoRaw = getVal('estado', 2);
            const fechaInicioRaw = getVal('fechaInicio', 3);
            const fechaFinRaw = getVal('fechaFin', 4);
            const motivoRaw = getVal('motivo', 5);
            const periodoRaw = getVal('periodo', 6);
            const totalDiasRaw = getVal('totalDias', 7);
            const pdfUrlRaw = getVal('pdfUrl', 8);

            registro = {
              dni: clean(dniRaw),
              nombreCompleto: clean(nombreRaw),
              estado: clean(estadoRaw) || "GOZADA",
              fechaInicio: parseExcelDate(fechaInicioRaw),
              fechaFin: parseExcelDate(fechaFinRaw),
              motivo: clean(motivoRaw),
              periodo: clean(periodoRaw),
              totalDias: Number(totalDiasRaw) || 0,
              pdfUrl: clean(pdfUrlRaw),
              fechaRegistro: serverTimestamp(),
              cargaMasiva: true,
            };
          } else {
            const colMap = item.columnMap;
            const getVal = (field, defaultIdx) => {
              const idx = colMap?.[field] !== undefined ? colMap[field] : defaultIdx;
              return row[idx];
            };

            const dniRaw = getVal('dni', 0);
            const nombreRaw = getVal('nombre', 1);
            const estadoRaw = getVal('estado', 2);
            const fechaInicioPeriodoRaw = getVal('fechaInicioPeriodo', 3);
            const fechaFinPeriodoRaw = getVal('fechaFinPeriodo', 4);
            const fechaVencimientoRaw = getVal('fechaVencimiento', 5);
            const periodoRaw = getVal('periodo', 6);
            const diasGanadosRaw = getVal('diasGanados', 7);
            const observacionesRaw = getVal('observaciones', 8);
            const regimenRaw = getVal('regimen', 9);

            registro = {
              dni: clean(dniRaw),
              nombreCompleto: clean(nombreRaw),
              estado: clean(estadoRaw) || "vigente",
              fechaInicioPeriodo: parseExcelDate(fechaInicioPeriodoRaw),
              fechaFinPeriodo: parseExcelDate(fechaFinPeriodoRaw),
              fechaVencimiento: parseExcelDate(fechaVencimientoRaw),
              periodo: clean(periodoRaw),
              diasGanados: Number(diasGanadosRaw) || 0,
              observaciones: clean(observacionesRaw),
              regimen: clean(regimenRaw),
              fechaRegistro: serverTimestamp(),
            };
          }

          // Generar ID determinista
          let docId = "";
          if (tipoVacacion === "gozadas") {
            const fechaKey = registro.fechaInicio ? registro.fechaInicio.getTime() : "SF";
            docId = `GOZ_${registro.dni}_${registro.periodo}_${fechaKey}`;
          } else {
            docId = `PEN_${registro.dni}_${registro.periodo}`;
          }

          const ref = doc(db, `vacaciones/${registro.dni}/${tipoVacacion}`, docId);
          await setDoc(ref, registro);

          newResults.push({
            dni: registro.dni,
            nombreCompleto: registro.nombreCompleto,
            estado: registro.estado,
            periodo: registro.periodo,
            status: "Registrado/Actualizado",
            success: true,
          });
          successCount++;
        } catch (error) {
          console.error("Error al registrar:", error);
          newResults.push({
            dni: registro.dni || "Desconocido",
            nombreCompleto: registro.nombreCompleto || "Desconocido",
            estado: registro.estado || "Error",
            periodo: registro.periodo || "-",
            status: "Error al registrar",
            success: false,
          });
          failedCount++;
        }
      }));

      // Actualizar progreso tras el lote
      const processedSoFar = Math.min(i + BATCH_SIZE, totalRows);
      setUploadStats({
        total: totalRows,
        processed: processedSoFar,
        successful: successCount,
        failed: failedCount
      });
      setUploadProgress(Math.round((processedSoFar / totalRows) * 100));

      // Yield al event loop para no congelar la UI
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    setResults(newResults);
    setLoading(false);

    // AUDITORÍA MASIVA
    try {
      await logBusinessAction("CARGA_MASIVA_AUSENCIAS", "GestionAusencias", {
        cambiosRealizados: {
          cargaVacaciones: { anterior: null, nuevo: `Se procesaron ${successCount} registros exitosos de ${tipoVacacion}` }
        },
        estadisticas: {
          exitosos: successCount,
          fallidos: failedCount,
          tipo: tipoVacacion
        }
      }, usuario);
    } catch (e) {
      console.warn("Error al registrar auditoría:", e);
    }

    const endTime = performance.now();
    const duration = ((endTime - startTime) / 1000).toFixed(2);

    // Al finalizar, mostrar resumen con Swal
    Swal.fire({
      title: 'Carga Completada',
      html: `
        <div class="text-start">
          <p>Proceso finalizado en <strong>${duration} segundos</strong>.</p>
          <ul>
            <li>Exitosos: <strong style="color: #28a745;">${successCount}</strong></li>
            <li>Fallidos: <strong style="color: #dc3545;">${failedCount}</strong></li>
          </ul>
        </div>
      `,
      icon: failedCount === 0 ? 'success' : 'warning',
      confirmButtonText: 'Aceptar'
    });
  };

  // Descargar filas con errores
  const handleDownloadErrors = () => {
    const errorRows = previewData.filter(item => !item.isValid).map(item => {
      // Reconstruir objeto con los datos originales + columna de error
      const row = item.row;
      const errorMsg = item.errors.join("; ");

      if (tipoVacacion === "gozadas") {
        return {
          DNI: row[0],
          Nombre: row[1],
          Estado: row[2],
          FechaInicio: row[3],
          FechaFin: row[4],
          Motivo: row[5],
          Periodo: row[6],
          TotalDias: row[7],
          PDF: row[8],
          ERRORES: errorMsg
        };
      } else {
        return {
          DNI: row[0],
          Nombre: row[1],
          Estado: row[2],
          InicioPeriodo: row[3],
          FinPeriodo: row[4],
          Vencimiento: row[5],
          Periodo: row[6],
          DiasGanados: row[7],
          Observaciones: row[8],
          Regimen: row[9],
          ERRORES: errorMsg
        };
      }
    });

    if (errorRows.length === 0) {
      Swal.fire('Info', 'No hay errores para descargar', 'info');
      return;
    }

    const ws = XLSX.utils.json_to_sheet(errorRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Errores");
    XLSX.writeFile(wb, `errores_vacaciones_${tipoVacacion}.xlsx`);
  };

  const handleDownloadResults = () => {
    if (results.length === 0) return;
    const ws = XLSX.utils.json_to_sheet(results);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Resultados");
    XLSX.writeFile(wb, `resultados_carga_${new Date().getTime()}.xlsx`);
  };

  // Descarga la plantilla Excel según el tipo de vacación
  const handleDownloadTemplate = () => {
    let legend = [];
    let headers = [];
    let example = [];
    if (tipoVacacion === "gozadas") {
      legend = [
        "Las columnas 'Motivo' y 'PDF URL' son opcionales. Puede dejarlas vacías si no aplica.",
      ];
      headers = [
        "DNI",
        "Nombre Completo",
        "Estado",
        "Fecha Inicio",
        "Fecha Fin",
        "Motivo (opcional)",
        "Periodo",
        "Total Días",
        "PDF URL (opcional)",
      ];
      example = [
        "41607220",
        "Oscar Cristh Rodriguez Arce",
        "GOZADA",
        "01/07/2025",
        "15/07/2025",
        "Vacaciones programadas",
        "2024",
        "15",
        "https://ejemplo.com/archivo.pdf",
      ];
    } else {
      legend = [];
      headers = [
        "DNI",
        "Nombre Completo",
        "Estado",
        "Fecha Inicio Periodo",
        "Fecha Fin Periodo",
        "Fecha Vencimiento",
        "Periodo",
        "Días Ganados",
        "Observaciones",
        "Régimen",
      ];
      example = [
        "41607220",
        "Oscar Cristh Rodriguez Arce",
        "vigente",
        "01/01/2023",
        "31/12/2023",
        "31/12/2024",
        "2023",
        "30",
        "Vacaciones del período 2023",
        "general",
      ];
    }

    const ws = XLSX.utils.aoa_to_sheet(
      legend.length > 0 ? [legend, headers, example] : [headers, example]
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Plantilla");

    XLSX.writeFile(wb, `plantilla_vacaciones_${tipoVacacion}.xlsx`);
  };

  // Descarga los registros pendientes en Excel
  const handleDownloadPendientes = async () => {
    setLoading(true);
    try {
      const pendientesRef = collection(db, `vacaciones`);
      let allRows = [];

      // Buscar todos los empleados de la empresa
      const usuariosRef = collection(db, "users");
      const q = query(usuariosRef, where("rucEmpresa", "==", usuario.ruc));
      const usuariosSnap = await getDocs(q);

      let dnis = usuariosSnap.docs.map((doc) => doc.data().dni).filter(Boolean);

      // Si no se encuentran empleados por rucEmpresa (ej. admin global o error data), fallback al propio usuario o array vacío
      if (dnis.length === 0 && usuario?.dni) {
        dnis = [usuario.dni];
      }

      for (const dni of dnis) {
        const colRef = collection(db, `vacaciones/${dni}/pendientes`);
        const snap = await getDocs(colRef);
        snap.forEach((doc) => {
          const data = doc.data();
          allRows.push([
            data.dni,
            data.nombreCompleto,
            data.estado,
            data.fechaInicioPeriodo?.toDate
              ? data.fechaInicioPeriodo.toDate().toLocaleString("es-PE")
              : "",
            data.fechaFinPeriodo?.toDate
              ? data.fechaFinPeriodo.toDate().toLocaleString("es-PE")
              : "",
            data.fechaVencimiento?.toDate
              ? data.fechaVencimiento.toDate().toLocaleString("es-PE")
              : "",
            data.periodo,
            data.diasGanados,
            data.observaciones,
            data.regimen,
          ]);
        });
      }

      const headers = [
        "DNI",
        "Nombre Completo",
        "Estado",
        "Fecha Inicio Periodo",
        "Fecha Fin Periodo",
        "Fecha Vencimiento",
        "Periodo",
        "Días Ganados",
        "Observaciones",
        "Régimen",
      ];
      const ws = XLSX.utils.aoa_to_sheet([headers, ...allRows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Pendientes");
      XLSX.writeFile(wb, "vacaciones_pendientes.xlsx");
    } catch (error) {
      alert("Error al descargar pendientes");
    }
    setLoading(false);
  };

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffffff 0%, #ffffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* Marco principal */}
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(45deg, #2c3e50, #3498db)",
              borderBottom: "3px solid #f39c12",
            }}
          >
          <div
            className="position-absolute end-0 top-50 translate-middle-y me-4"
            style={{ zIndex: 10 }}
          >
            <BotonManualHeader />
          </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaCloudUploadAlt className="me-3" />
                Carga Masiva de Vacaciones
              </h2>
              <p className="mb-0 opacity-75">
                Cargue múltiples registros de vacaciones desde Excel
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Plantilla Excel */}
            <Card className="shadow-sm border-0 mb-4">
              <Card.Body className="p-4">
                <div className="d-flex align-items-center mb-3">
                  <FaFileExcel className="text-success me-3 fs-2" />
                  <div>
                    <h5 className="fw-bold mb-1">Plantilla Excel</h5>
                    <p className="mb-0 text-muted">
                      Descarga la plantilla y complétala con la información de
                      las vacaciones
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline-success"
                  onClick={handleDownloadTemplate}
                  style={{
                    borderRadius: "10px",
                    padding: "10px 20px",
                  }}
                >
                  <FaDownload className="me-2" />
                  Descargar Plantilla Vacaciones Gozadas
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() => {
                    const currentTipo = tipoVacacion;
                    setTipoVacacion("pendientes");
                    setTimeout(() => {
                      handleDownloadTemplate();
                      setTipoVacacion(currentTipo);
                    }, 0);
                  }}
                  style={{
                    borderRadius: "10px",
                    padding: "10px 20px",
                    marginLeft: "15px",
                  }}
                >
                  <FaDownload className="me-2" />
                  Descargar Plantilla Vacaciones Pendientes/Ganadas
                </Button>
              </Card.Body>
            </Card>

            {/* Formulario de carga */}
            <Card className="shadow-sm border-0 mb-4">
              <Card.Body className="p-4">
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold text-dark">
                        <FaFileExcel className="me-2" />
                        Tipo de Vacación
                      </Form.Label>
                      <Form.Select
                        value={tipoVacacion}
                        onChange={(e) => setTipoVacacion(e.target.value)}
                        className="form-control-lg"
                        style={{ borderRadius: "10px" }}
                      >
                        <option value="gozadas">Vacaciones Gozadas</option>
                        <option value="pendientes">
                          Vacaciones Pendientes/Ganadas
                        </option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold text-dark">
                        <FaUpload className="me-2" />
                        Archivo Excel
                      </Form.Label>
                      <Form.Control
                        type="file"
                        onChange={handleFileChange}
                        className="form-control-lg"
                        accept=".xlsx,.xls"
                        style={{ borderRadius: "10px" }}
                      />
                      <Form.Text className="text-muted">
                        Selecciona el archivo Excel con los datos de vacaciones
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                {!showPreview && (
                  <Button
                    variant="primary"
                    onClick={handleUpload}
                    disabled={!selectedFile || loading}
                    size="lg"
                    style={{
                      borderRadius: "10px",
                      background: "linear-gradient(45deg, #3498db, #2980b9)",
                      border: "none",
                      padding: "12px 30px",
                    }}
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Procesando...
                      </>
                    ) : (
                      <>
                        <FaCloudUploadAlt className="me-2" />
                        Procesar Vacaciones
                      </>
                    )}
                  </Button>
                )}
              </Card.Body>
            </Card>

            {/* Indicador de carga de pantalla completa */}
            {loading && (
              <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                style={{
                  zIndex: 1080,
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  backdropFilter: "blur(5px)",
                }}
              >
                <div className="text-center text-white">
                  <Spinner
                    animation="border"
                    variant="light"
                    style={{ width: "4rem", height: "4rem" }}
                  />
                  <h4 className="mt-3 mb-1 fw-bold">Procesando registros...</h4>
                  <p className="mb-0 opacity-75">
                    Por favor espere mientras se cargan los datos
                  </p>

                  {uploadProgress > 0 && (
                    <div className="mt-4" style={{ width: "300px" }}>
                      <ProgressBar
                        now={uploadProgress}
                        label={`${uploadProgress}%`}
                        variant="success"
                        animated
                        style={{ height: "20px" }}
                      />
                      <div className="mt-2 text-white small opacity-75">
                        {uploadStats.processed} de {uploadStats.total} registros procesados
                        <br />
                        ✓ {uploadStats.successful} exitosos | ✗ {uploadStats.failed} fallidos
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Vista Previa de Datos */}
            {showPreview && previewData.length > 0 && (
              <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-info text-white py-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <h5 className="mb-0 fw-semibold">
                      <FaEye className="me-2" />
                      Vista Previa - {previewData.length} registros encontrados
                    </h5>
                    <div>
                      <Badge bg="success" className="me-2">
                        {previewData.filter(p => p.isValid).length} válidos
                      </Badge>
                      <Badge bg="danger">
                        {previewData.filter(p => !p.isValid).length} con errores
                      </Badge>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  {/* Errores de validación */}
                  {validationErrors.length > 0 && (
                    <Alert variant="danger" className="m-3 mb-0">
                      <strong>
                        <FaExclamationTriangle className="me-2" />
                        Se encontraron {validationErrors.length} errores:
                      </strong>
                      <ul className="mb-0 mt-2" style={{ maxHeight: "200px", overflowY: "auto" }}>
                        {validationErrors.map((error, idx) => (
                          <li key={idx}>{error}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}

                  {/* Tabla de vista previa */}
                  <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                    <Table responsive hover className="mb-0">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th className="fw-semibold border-0">#</th>
                          <th className="fw-semibold border-0">Estado</th>
                          <th className="fw-semibold border-0">DNI</th>
                          <th className="fw-semibold border-0">Nombre</th>
                          {tipoVacacion === "gozadas" ? (
                            <>
                              <th className="fw-semibold border-0">Fecha Inicio</th>
                              <th className="fw-semibold border-0">Fecha Fin</th>
                              <th className="fw-semibold border-0">Días</th>
                            </>
                          ) : (
                            <>
                              <th className="fw-semibold border-0">Periodo</th>
                              <th className="fw-semibold border-0">Días Ganados</th>
                              <th className="fw-semibold border-0">Régimen</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.map((item, idx) => {
                          const row = item.row;
                          const colMap = item.columnMap;

                          // Helper para obtener valor usando columnMap
                          const getVal = (field, defaultIdx) => {
                            const idx = colMap?.[field] !== undefined ? colMap[field] : defaultIdx;
                            return row[idx];
                          };

                          return (
                            <tr
                              key={idx}
                              className={!item.isValid ? "table-danger" : ""}
                            >
                              <td>{idx + 1}</td>
                              <td>
                                {item.isValid ? (
                                  <FaCheckCircle className="text-success" />
                                ) : (
                                  <FaTimesCircle className="text-danger" />
                                )}
                              </td>
                              <td>{formatCellValue(getVal('dni', 0))}</td>
                              <td>{formatCellValue(getVal('nombre', 1))}</td>
                              {tipoVacacion === "gozadas" ? (
                                <>
                                  <td>{formatCellValue(getVal('fechaInicio', 3))}</td>
                                  <td>{formatCellValue(getVal('fechaFin', 4))}</td>
                                  <td>{formatCellValue(getVal('totalDias', 7))}</td>
                                </>
                              ) : (
                                <>
                                  <td>{formatCellValue(getVal('periodo', 6))}</td>
                                  <td>{formatCellValue(getVal('diasGanados', 7))}</td>
                                  <td>{formatCellValue(getVal('regimen', 9))}</td>
                                </>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>

                  {/* Botones de acción */}
                  <div className="p-3 border-top bg-light">
                    <div className="d-flex justify-content-between">
                      <Button
                        variant="outline-secondary"
                        onClick={() => {
                          setShowPreview(false);
                          setPreviewData([]);
                          setValidationErrors([]);
                          setSelectedFile(null);
                        }}
                      >
                        Cancelar
                      </Button>

                      {previewData.some(p => !p.isValid) && (
                        <Button
                          variant="outline-danger"
                          className="mx-2"
                          onClick={handleDownloadErrors}
                        >
                          <FaExclamationTriangle className="me-2" />
                          Descargar Errores
                        </Button>
                      )}

                      <Button
                        variant="primary"
                        onClick={handleConfirmUpload}
                        disabled={previewData.filter(p => p.isValid).length === 0}
                      >
                        <FaCheckCircle className="me-2" />
                        Confirmar y Procesar ({previewData.filter(p => p.isValid).length} registros válidos)
                      </Button>
                    </div>
                    {validationErrors.length > 0 && (
                      <Alert variant="warning" className="mt-3 mb-0">
                        <small>
                          <strong>Nota:</strong> Solo se procesarán los registros válidos.
                          Los registros con errores serán omitidos.
                        </small>
                      </Alert>
                    )}
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Resultados */}
            {results.length > 0 && (
              <Card
                className="shadow-sm border-0"
                style={{ borderRadius: "15px" }}
              >
                <Card.Header className="bg-success text-white py-3">
                  <div className="d-flex align-items-center">
                    <FaCheckCircle className="me-2" />
                    <h5 className="mb-0 fw-semibold">Resultados de la Carga</h5>

                    <Button
                      variant="light"
                      size="sm"
                      className="ms-3 text-success fw-bold border-0"
                      onClick={handleDownloadResults}
                      title="Descargar reporte de resultados"
                    >
                      <FaDownload className="me-1" /> Reporte
                    </Button>

                    <Badge bg="light" text="success" className="ms-auto">
                      {results.filter((r) => r.success).length} exitosos /{" "}
                      {results.length} total
                    </Badge>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                    <Table responsive hover className="mb-0">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th className="fw-semibold border-0 text-center">
                            #
                          </th>
                          <th className="fw-semibold border-0">DNI</th>
                          <th className="fw-semibold border-0">
                            Nombre Completo
                          </th>
                          <th className="fw-semibold border-0">Estado</th>
                          <th className="fw-semibold border-0">Periodo</th>
                          <th className="fw-semibold border-0 text-center">
                            Resultado
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.map((res, index) => (
                          <tr key={index}>
                            <td className="text-center fw-medium">
                              {index + 1}
                            </td>
                            <td className="fw-medium">{res.dni}</td>
                            <td>{res.nombreCompleto}</td>
                            <td>
                              <Badge bg={res.success ? "success" : "warning"}>
                                {res.estado}
                              </Badge>
                            </td>
                            <td>{res.periodo}</td>
                            <td className="text-center">
                              <div className="d-flex align-items-center justify-content-center">
                                {res.success ? (
                                  <FaCheckCircle className="text-success me-2" />
                                ) : (
                                  <FaTimesCircle className="text-danger me-2" />
                                )}
                                <span
                                  className={
                                    res.success
                                      ? "text-success fw-medium"
                                      : "text-danger fw-medium"
                                  }
                                >
                                  {res.status}
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Resumen cuando hay resultados */}
            {results.length > 0 && (
              <Alert
                variant="info"
                className="border-0 shadow-sm mt-4"
                style={{ borderRadius: "10px" }}
              >
                <div className="d-flex align-items-center">
                  <FaCheckCircle className="me-3 fs-4" />
                  <div>
                    <h5 className="fw-bold mb-1">Proceso Completado</h5>
                    <p className="mb-0">
                      Se procesaron {results.length} registros:{" "}
                      <Badge bg="success" className="me-2">
                        {results.filter((r) => r.success).length} exitosos
                      </Badge>
                      <Badge bg="danger">
                        {results.filter((r) => !r.success).length} fallidos
                      </Badge>
                    </p>
                  </div>
                </div>
              </Alert>
            )}
            {/* Nota sobre incremento de días */}
            <Alert
              variant="warning"
              className="mt-3"
              style={{ borderRadius: "10px" }}
            >
              <strong>Importante:</strong> Luego de la carga masiva de
              vacaciones pendientes/ganadas, se incrementará automáticamente{" "}
              <strong>2.5 días por mes</strong> para el régimen general/construcción civil y <strong>1.25 días por mes</strong> para pequeña empresa/régimen agrario en el sistema.
            </Alert>
          </Card.Body>

        </Card>
      </Container>
    </div>
  );
}

export default VacacionesMasivas;